import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { WelcomePlan } from "@/pages/WelcomePlan";
import { BillingSuccess } from "@/pages/BillingSuccess";
import { Questionnaire } from "@/pages/Questionnaire";
import { Provisioning } from "@/pages/Provisioning";
import { Dashboard } from "@/pages/Dashboard";
import { Subscription } from "@/pages/Subscription";
import { CustomDomain } from "@/pages/CustomDomain";
import { ImportAirbnb } from "@/pages/ImportAirbnb";
import { announceHeight, announceLocation, installPostMessageListeners, startVersionWatch } from "@/lib/post-message";
import { applyMarketingIntent } from "@/lib/marketing-intent";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

function LocationSync() {
  const location = useLocation();
  useEffect(() => {
    announceLocation(location.pathname + location.search);
  }, [location]);
  return null;
}

function HeightSync() {
  // ResizeObserver fires on every layout change in the root; we throttle via
  // requestAnimationFrame inside announceHeight.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => announceHeight());
    ro.observe(document.documentElement);
    announceHeight();
    return () => ro.disconnect();
  }, []);
  return null;
}

function ParentBridge() {
  useEffect(() => {
    return installPostMessageListeners({
      onNavigate: (path) => {
        // A host link can carry a plan / site type for an app that is already
        // loaded; take it before the router redirects the query away.
        const q = path.indexOf("?");
        if (q !== -1) applyMarketingIntent(path.slice(q));
        // Parent-driven nav uses history.replaceState so we don't push a new
        // history frame for what's essentially the same location.
        window.history.replaceState(null, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      },
    });
  }, []);
  // Watch for a newer deploy of this app and let the host offer a reload
  // (contract v1.13). No-op unless embedded with a stamped build version.
  useEffect(() => startVersionWatch(), []);
  return null;
}

// The plan and site type chosen on rumio.world arrive on the boot URL. Read
// once at module load: the root route redirects to /welcome on first render,
// and that redirect drops the query string.
if (typeof window !== "undefined") applyMarketingIntent(window.location.search);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ParentBridge />
        <LocationSync />
        <HeightSync />
        <Routes>
          <Route path="/" element={<Navigate to="/welcome" replace />} />
          <Route path="/welcome" element={<WelcomePlan />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/questionnaire" element={<Questionnaire />} />
          <Route path="/provisioning/:jobId" element={<Provisioning />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/custom-domain" element={<CustomDomain />} />
          <Route path="/import-airbnb" element={<ImportAirbnb />} />
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
